import * as Sentry from '@sentry/react';
import {
  useEffect,
} from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import { getAnalyticsEnvironment } from '@core/analytics/getEnvironment';

const environment = getAnalyticsEnvironment();

/** The pay link's path is a bearer token: it reads the booking and pays its deposit difference. */
const PAY_LINK_PATH = '/guarantee-adjustment/';
/** Nothing in the app links to the pay link: a session that shows it landed on it. */
const LANDED_ON_PAY_LINK = typeof window !== 'undefined' && window.location.pathname.startsWith(PAY_LINK_PATH);

const scrubUrl = (value: string): string => value
  .replace(/([?&](authToken|token)=)[^&#]+/gi, '$1[REDACTED]')
  .replace(/\/auth\/[A-Za-z0-9._~+/=-]+/g, '/auth/[REDACTED]')
  .replace(/\/guarantee-adjustment\/[^/?#&\s]+/g, `${PAY_LINK_PATH}[REDACTED]`);

const URL_KEYS = ['url', 'from', 'to', 'http.url', 'url.full', 'transaction', 'endpoint', 'returnUrl'];
/** The contexts captureApiError, capturePaymentError and captureAuthError add, each with its caller's context in. */
const CAPTURE_CONTEXTS = ['api_request', 'payment', 'error_context', 'auth'];

function scrubUrlFields(data: Record<string, unknown> | undefined): void {
  if (!data) return;
  for (const key of URL_KEYS) {
    const value = data[key];
    if (typeof value === 'string') data[key] = scrubUrl(value);
  }
}

/** Redacts token URLs from a span sent on its own (INP) or in a transaction. */
function scrubSpanUrls<T extends { description?: string; data?: unknown }>(span: T): T {
  if (span.description) span.description = scrubUrl(span.description);
  scrubUrlFields(span.data as Record<string, unknown> | undefined);
  return span;
}

/** Redacts token URLs wherever the SDK and our captures put them, on an error or a transaction. */
function scrubEventUrls<T extends Sentry.Event>(event: T): T {
  if (event.request?.url) event.request.url = scrubUrl(event.request.url);
  const headers = event.request?.headers ?? {};
  for (const name of Object.keys(headers)) {
    if (/^referer$/i.test(name)) headers[name] = scrubUrl(headers[name]);
  }
  if (event.transaction) event.transaction = scrubUrl(event.transaction);
  for (const name of CAPTURE_CONTEXTS) scrubUrlFields(event.contexts?.[name] as Record<string, unknown> | undefined);
  scrubUrlFields(event.contexts?.trace?.data as Record<string, unknown> | undefined);
  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (breadcrumb.message) breadcrumb.message = scrubUrl(breadcrumb.message); // apiClient's auth breadcrumbs: the URL
    scrubUrlFields(breadcrumb.data);
  }
  for (const span of event.spans ?? []) scrubSpanUrls(span);
  return event;
}

/**
 * Filter predicate for Sentry breadcrumbs.
 *
 * Drops noisy-but-uninteresting entries so the signal-to-noise ratio in
 * captured events stays high. Exported for unit tests. Returns null to drop,
 * or the breadcrumb unchanged.
 */
export function filterBreadcrumb(
  breadcrumb: Sentry.Breadcrumb,
): Sentry.Breadcrumb | null {
  const data = breadcrumb.data as Record<string, unknown> | undefined;
  const url = typeof data?.url === 'string' ? data.url : '';
  const status = typeof data?.status_code === 'number' ? data.status_code : undefined;
  if (
    (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
    url.includes('/locales/') &&
    status === 200
  ) {
    return null;
  }
  return breadcrumb;
}

/**
 * Detects onerror events from cross-origin scripts whose stack the browser
 * redacted. The browser delivers a single frame with `filename: "undefined"`
 * (literal string or missing), no function, and no column-meaningful symbol —
 * identical across every event because the platform strips the payload before
 * our handler sees it. Typical sources are iOS Safari extensions, in-app
 * browsers, and third-party SDKs loaded without CORS headers. The result is
 * a high-volume issue with no actionable signal.
 */
export function isOpaqueCrossOriginError(event: Sentry.Event): boolean {
  const values = event.exception?.values;
  if (!values || values.length !== 1) return false;
  const frames = values[0].stacktrace?.frames;
  if (!frames || frames.length !== 1) return false;
  const frame = frames[0];
  const filenameMissing =
    frame.filename == null ||
    frame.filename === 'undefined' ||
    frame.filename === '<anonymous>';
  return filenameMissing && !frame.function && !frame.module;
}

/**
 * Detects errors whose in-app frames all come from third-party scripts injected
 * by in-app WebViews (Instagram/Facebook ContactAutofillBridge,
 * webkit.messageHandlers, TikTok postMessage shims, etc.). These scripts run
 * inside the restaurant page but live outside our bundle, so every in-app
 * frame resolves to filename="<anonymous>" with named functions from the
 * injector (e.g. `setContactAutofillValuesFromBridge`).
 *
 * Unlike `isOpaqueCrossOriginError`, this covers multi-frame stacks — the
 * single-frame predicate misses them because the WebView runtime preserves the
 * call chain.
 *
 * Discriminator: every in-app frame has filename === "<anonymous>". Our bundle
 * frames carry a real URL (e.g. `/assets/index-<hash>.js` or a full CDN URL),
 * which we observe directly in Sentry payloads with cached bundles — source
 * maps are resolved server-side during ingestion, so the filename we see in
 * beforeSend is the raw Error.stack URL, never the `/src/...` path. Injected
 * bridge functions always evaluate without a script URL and show up as
 * `<anonymous>`.
 */
export function isThirdPartyAnonymousError(event: Sentry.Event): boolean {
  const values = event.exception?.values;
  if (!values || values.length === 0) return false;
  // Single-pass scan: exits as soon as any in-app frame has a real bundle
  // URL (our code). Runs inside beforeSend on every captured event, so the
  // two-boolean loop beats flatMap+filter+some on short stacks where the
  // decisive frame is usually near the top.
  let sawInAppFrame = false;
  for (const value of values) {
    const frames = value.stacktrace?.frames;
    if (!frames) continue;
    for (const frame of frames) {
      if (!frame.in_app) continue;
      sawInAppFrame = true;
      if (typeof frame.filename === 'string' && frame.filename !== '<anonymous>') {
        return false;
      }
    }
  }
  return sawInAppFrame;
}

/**
 * URL schemes used by scripts injected into the page by the host browser
 * environment — neither our bundle nor the page's third-party SDKs.
 *
 *   - `iabjs://` — Facebook / Instagram in-app browser autofill + tracking shims
 *   - `chrome-extension://` — Chrome extensions (content scripts, autofill, etc.)
 *   - `moz-extension://` — Firefox extensions
 *   - `safari-extension://` / `safari-web-extension://` — Safari extensions
 *   - `webkit-masked-url://` — Safari/iOS mask for cross-origin scripts (iOS 17+)
 *   - `webkit-bundle-resource://` / `safari-resource://` / `wkbundle://` — iOS
 *     WebKit internal resources surfacing through `window.onerror`
 *   - `ms-browser-extension://` — Edge (legacy + MV3)
 *   - `qrc://` — Qt WebEngine resource wrapper
 *
 * Hoisted to module scope so the literal isn't re-instantiated per event in
 * the hot `beforeSend` path.
 */
const INJECTED_SCRIPT_SCHEME_RE =
  /^(iabjs|chrome-extension|moz-extension|safari-extension|safari-web-extension|webkit-masked-url|webkit-bundle-resource|safari-resource|wkbundle|ms-browser-extension|qrc):\/\//;

/**
 * Detects errors thrown by scripts injected into the page by the host browser
 * environment. See `INJECTED_SCRIPT_SCHEME_RE` above for the scheme list.
 *
 * These scripts execute against the page's window object so `window.onerror`
 * picks them up, but they're outside our control: the user has installed an
 * extension or is browsing inside a host app's WebView. We can neither fix
 * nor reproduce them. Filtering at ingest keeps the project's error feed
 * actionable (Sentry MEITRE-FRONTEND-V2-B5 — `SyntaxError: Invalid or
 * unexpected token` from `iabjs://autofill_contact_enhanced` — is the
 * concrete trigger for this filter).
 *
 * Filter shape: every frame's filename uses one of these schemes. If a frame
 * traces back to our bundle, we keep the event — even an extension-triggered
 * error inside our code is ours to fix.
 *
 * Fail-safe: if a future Sentry SDK ever moves `filename` off the frame
 * (current type guarantees a string when present), the `typeof !== 'string'`
 * branch returns `false` and the event flows through. Better to leak some
 * noise than silently drop genuine errors.
 */
export function isInjectedBrowserScriptError(event: Sentry.Event): boolean {
  const values = event.exception?.values;
  if (!values || values.length === 0) return false;
  let sawFrame = false;
  for (const value of values) {
    const frames = value.stacktrace?.frames;
    if (!frames || frames.length === 0) continue;
    for (const frame of frames) {
      sawFrame = true;
      if (typeof frame.filename !== 'string') return false;
      if (!INJECTED_SCRIPT_SCHEME_RE.test(frame.filename)) return false;
    }
  }
  return sawFrame;
}

/**
 * Extracts the hostname from `document.referrer` for use as a Sentry custom tag.
 *
 * Why: Sentry's org-level PII scrubber replaces the `Referer` header value in
 * `event.request.headers` with `[Filtered]`, hiding the inbound traffic source.
 * Custom tags are NOT subject to that scrubber, so we can promote the hostname
 * (no path, no query, no fragments) safely.
 *
 * Returns:
 *  - The hostname (e.g. `instagram.com`, `mail.meitre.com`) when `document.referrer`
 *    is a parseable URL.
 *  - `null` when there's no referrer (direct nav, bookmark, autocomplete),
 *    when the referrer is malformed, or when `document` is unavailable (SSR/test
 *    edge cases). Caller should skip the tag in that case.
 *
 * PII safety: hostname only. We never read path, query, or fragment. Even tracker
 * domains that include user identifiers in path (e.g. `mail.example.com/click/abc123/`)
 * are exposed only as `mail.example.com`.
 */
export function getRefererHostFromString(referrer: string | undefined | null): string | null {
  if (!referrer || typeof referrer !== 'string') return null;
  try {
    const u = new URL(referrer);
    // Filter browser-extension protocols. URL.hostname for these returns the
    // extension ID (e.g. `chrome-extension://abcdef/page` → `abcdef`), which is
    // not a meaningful traffic source and just pollutes tag cardinality.
    // (The injected-script Sentry filter already drops errors FROM these
    // schemes; this prevents extension-installed users from having every event
    // tagged with their extension ID.)
    if (/^(chrome|moz|safari|safari-web|ms-browser|edge)-extension:$/i.test(u.protocol)) {
      return null;
    }
    return u.hostname || null;
  } catch {
    return null;
  }
}

/**
 * Allow-list of Meitre subdomains whose path is considered safe to log.
 *
 * `.endsWith('.meitre.com')` was rejected as too broad: a future marketing /
 * status / help subdomain (e.g. tracking.meitre.com) could be spun up with
 * user IDs in path, and the telemetry would silently start capturing them.
 * Explicit list keeps the contract auditable. Restaurant tenants share the
 * `<slug>.meitre.com` shape; we accept those because the booking-app routes
 * are PII-safe by design (paths are bookmarkable + shareable: /, /select-time,
 * /payment, /confirmation, ...).
 */
const INTERNAL_REFERRER_HOST_ALLOWLIST = new Set([
  'auth.v2.meitre.com',
  'auth.staging.v2.meitre.com',
]);

function isAllowedInternalRefererHost(host: string): boolean {
  if (INTERNAL_REFERRER_HOST_ALLOWLIST.has(host)) return true;
  // Restaurant tenants: <slug>.meitre.com with exactly one subdomain label.
  // Excludes multi-label subdomains like `help.docs.meitre.com` or any deep
  // marketing subdomain that didn't exist when this code was written.
  // The regex below matches a single DNS-safe label + `.meitre.com`.
  return /^[a-z0-9-]+\.meitre\.com$/i.test(host);
}

/**
 * Internal-domain referrer details. Captured ONLY when the referrer host is in
 * the explicit allowlist above — never for external referrers, which could
 * carry user-identifiable path segments. Investigating Sentry
 * MEITRE-FRONTEND-V2-BF Sub-Y (96% of /payment guard fires have
 * referer_host=auth.v2 but we don't know which auth page or what return_url
 * triggered the inbound nav).
 *
 * Sanitization:
 *  - `path`: path component only. No query, no fragment.
 *  - `returnUrl`: the value of the `return_url` query param, with
 *    credential/PII-like nested params redacted, capped at 800 chars.
 *    Caller decides where to put this (we route to `event.contexts.referer`
 *    because Sentry tag values truncate to 200 chars server-side, which
 *    silently breaks the diagnostic for long return_urls).
 *
 * Returns nulls for non-allowlisted referrers, missing fields, or malformed
 * input.
 */
export function getInternalRefererInfoFromString(referrer: string | undefined | null): {
  host: string | null;
  path: string | null;
  returnUrl: string | null;
} {
  const empty = { host: null, path: null, returnUrl: null };
  const host = getRefererHostFromString(referrer);
  if (!host) return empty;
  // Only capture path / return_url for explicitly-allowlisted subdomains.
  // External referrers (Instagram, Gmail, etc.) AND any unexpected internal
  // subdomain stay host-only.
  if (!isAllowedInternalRefererHost(host)) return { host, path: null, returnUrl: null };
  try {
    const u = new URL(referrer as string);
    const path = u.pathname || '/';
    const returnUrlParam = u.searchParams.get('return_url');
    let returnUrl: string | null = null;
    if (returnUrlParam) {
      returnUrl = sanitizeReturnUrl(returnUrlParam);
    }
    return { host, path, returnUrl };
  } catch {
    return { host, path: null, returnUrl: null };
  }
}

/**
 * Defense-in-depth sanitizer for the `return_url` value before exposing it as
 * Sentry diagnostic data. The auth-subdomain flow only puts trusted Meitre
 * URLs here, but a) we cannot enforce that contract from the telemetry layer,
 * and b) some OAuth flows use URL fragments (`#access_token=...`) and
 * userinfo (`https://user:pass@…`) which would otherwise pass through.
 *
 * Handles both absolute (`https://...`) and relative (`/payment?...`) inputs
 * via a dummy-origin parsing trick — same redaction rules apply to both, so
 * `?return_url=/payment?email=user@x.com` cannot bypass the sanitizer by
 * being relative.
 *
 * Steps:
 *  1. Drop non-http(s) protocols → `<non-web-protocol>` sentinel.
 *  2. Strip fragment (`#...`) and userinfo (`https://user:pass@host/`).
 *  3. Redact any query param whose KEY name contains a credential-like or
 *     PII-like substring.
 *  4. Cap at 800 chars (long URLs land in `event.contexts`, not tags).
 */
const SANITIZE_DUMMY_ORIGIN = 'https://sanitize.invalid';

function sanitizeReturnUrl(returnUrlParam: string): string {
  // Parse as relative against a dummy origin. This way, relative inputs like
  // `/payment?email=…` are parseable and run through the same redaction loop
  // as absolute ones. If the input is already absolute, `new URL(input, base)`
  // ignores the base.
  let ru: URL;
  try {
    ru = new URL(returnUrlParam, SANITIZE_DUMMY_ORIGIN);
  } catch {
    // Truly unparseable (e.g. contains invalid chars). Truncate and return raw
    // so we can still see the shape in diagnostics.
    return returnUrlParam.slice(0, 800);
  }

  // Drop non-web protocols (javascript:, data:, file:, etc.). Note: relative
  // inputs inherit the dummy origin's https: protocol, so this only catches
  // absolute inputs with bad schemes.
  if (ru.protocol !== 'https:' && ru.protocol !== 'http:') {
    return '<non-web-protocol>';
  }

  ru.hash = '';
  ru.username = '';
  ru.password = '';

  // Substring match against the param KEY catches both credential-like names
  // (token, jwt, secret, password, key, session, sig, otp, bearer, code) and
  // common PII keys (email, phone, name, user, mail, tel, address, dob).
  // Safe-fail: false positives just redact a benign param.
  const sensitive = /(token|secret|password|key|auth|session|sig|otp|jwt|bearer|code|email|phone|name|user|mail|tel|address|dob)/i;
  ru.searchParams.forEach((_v, k) => {
    if (sensitive.test(k)) ru.searchParams.set(k, '<redacted>');
  });

  // If the input was relative, strip the dummy origin back off so we return
  // a relative-shaped string (matches what was in the original auth URL).
  const wasRelative = !/^[a-z][a-z0-9+.-]*:/i.test(returnUrlParam.trim());
  const out = wasRelative
    ? ru.toString().slice(SANITIZE_DUMMY_ORIGIN.length)
    : ru.toString();
  return out.slice(0, 800);
}

function getInternalRefererInfoSafe(): {
  host: string | null;
  path: string | null;
  returnUrl: string | null;
} {
  if (typeof document === 'undefined') return { host: null, path: null, returnUrl: null };
  return getInternalRefererInfoFromString(document.referrer);
}

/**
 * Detects errors thrown by the Sentry SDK itself — typically @sentry-internal/replay
 * or @sentry/browser internals failing on browser-imposed restrictions (cross-origin
 * iframes, missing APIs, etc.). rrweb (underlying replay recorder) has a known bug
 * where `onIframeLoad` throws SecurityError when encountering a frame that became
 * cross-origin after load. Fixed upstream in rrweb PR #1695 but not yet in our
 * Sentry version.
 *
 * Filter shape: every frame's filename traces back to a sentry package AND no frame
 * points into app code. If one of our components threw while calling Sentry APIs,
 * we want to keep that event, so we only drop when the stack is entirely inside
 * sentry/rrweb.
 */
export function isSentryInternalError(event: Sentry.Event): boolean {
  const values = event.exception?.values;
  if (!values || values.length === 0) return false;
  const SENTRY_PACKAGE_RE = /@sentry[-/]|node_modules\/rrweb/;
  for (const value of values) {
    const frames = value.stacktrace?.frames;
    if (!frames || frames.length === 0) return false;
    for (const frame of frames) {
      if (frame.in_app) return false;
      if (typeof frame.filename !== 'string') return false;
      if (!SENTRY_PACKAGE_RE.test(frame.filename)) return false;
    }
  }
  return true;
}

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN ||
    'https://0eae75a7be79a3b6fd79751218568286@o132507.ingest.us.sentry.io/4511101099900928',
  environment,
  release: import.meta.env.VITE_SENTRY_RELEASE || undefined,
  enabled: environment !== 'local',

  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
      // A replay keeps the page URL and the SDK has no hook to scrub it. Its buffer outlives a navigation, so a
      // session that landed on the pay link records no error replay at all.
      beforeErrorSampling: () => !LANDED_ON_PAY_LINK,
    }),
  ],

  tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: environment === 'production' ? 1.0 : 0,

  beforeSendTransaction: scrubEventUrls,
  beforeSendSpan: scrubSpanUrls,

  beforeSend(event, hint) {
    scrubEventUrls(event);

    // Drop errors from third-party scripts injected by in-app browsers (Instagram, TikTok, etc.)
    // Only drop if NO stack frame points to our own source files.
    // Check both hint.originalException (when available as Error) and the event
    // exception values (covers unhandled promise rejections where originalException
    // may not be an Error instance).
    const error = hint?.originalException;
    const messagePattern = /webkit\.messageHandlers|messageHandlers/;
    const matchesHint = error instanceof Error && messagePattern.test(error.message);
    const matchesEvent = event.exception?.values?.some(
      (v) => v.value && messagePattern.test(v.value),
    );
    if (matchesHint || matchesEvent) {
      const frames = event.exception?.values?.flatMap(
        (v) => v.stacktrace?.frames ?? [],
      );
      const hasAppFrame = frames?.some(
        (f) => f.in_app && f.filename && /\/src\//.test(f.filename),
      );
      if (!hasAppFrame) return null;
    }

    if (isOpaqueCrossOriginError(event)) return null;
    if (isThirdPartyAnonymousError(event)) return null;
    if (isInjectedBrowserScriptError(event)) return null;
    if (isSentryInternalError(event)) return null;

    // Promote document.referrer's hostname (no path, no query) to a custom tag.
    // Sentry's org-level PII scrubber masks `Referer` in request.headers but does
    // NOT scrub custom tags — so this gives us the inbound traffic source domain
    // for filtering and grouping without exposing any PII. Critical for
    // diagnosing Sentry MEITRE-FRONTEND-V2-BF Pattern A (~76% of BF events
    // have [FILTERED] referer; this unlocks visibility).
    const refererInfo = getInternalRefererInfoSafe();
    if (refererInfo.host) {
      event.tags = { ...event.tags, referer_host: refererInfo.host };
    }
    // For allowlisted Meitre subdomains, also expose the referrer path and any
    // `return_url` query param. Investigating BF Sub-Y: 96% of /payment guard
    // fires have referer_host=auth.v2 but we don't know WHICH auth page
    // (login / signup / forgot-password) or what return_url shape the user
    // came from. Same scrubber-bypass strategy.
    //
    // Routing:
    //  - `referer_path` → tag. Paths are short and enumerable; tag cardinality
    //    is bounded and they're queryable in Sentry alerts.
    //  - `referer.return_url` → context (NOT tag). Sentry truncates tag values
    //    to 200 chars server-side; a long return_url like
    //    `https://<slug>.meitre.com/payment?ps=<base64-bookingContext>` would
    //    get truncated mid-string, defeating the diagnostic. Contexts have no
    //    such cap.
    if (refererInfo.path) {
      // Defensive normalize for double-slash referer paths. Phase 9
      // (2026-05-26) found 6 events tagged `referer_path=//user/account`,
      // which traces to backend email templates emitting URLs like
      // `https://r.meitre.com//user/account`. main.tsx already
      // normalizes the address-bar URL on boot, but the browser's
      // `document.referrer` carries the original malformed path —
      // so the subsequent navigation to /booking-final inherits the
      // malformed value. Collapse repeated slashes so the tag groups
      // cleanly with `/user/account`. Real fix is in meitre-backend's
      // email templates; this is observability hygiene.
      const normalizedPath = scrubUrl(refererInfo.path.replace(/\/{2,}/g, '/'));
      event.tags = { ...event.tags, referer_path: normalizedPath };
    }
    if (refererInfo.returnUrl) {
      event.contexts = {
        ...event.contexts,
        referer: { return_url: scrubUrl(refererInfo.returnUrl) },
      };
    }

    // Instagram in-app WebView detection. Phase 9 (2026-05-26) audit of
    // Sentry MEITRE-FRONTEND-V2-BN bucketing found this tag was *intended*
    // by the original referer-tagging work but never actually emitted —
    // 0/515 events carried it. `browser.name == 'Instagram'` from Sentry's
    // built-in browser integration is the existing proxy (~19% of BN
    // events), but it isn't queryable as a boolean and conflates the
    // WebView with the Instagram desktop site. Set the tag explicitly
    // from the UA so alerts can filter on `is_instagram:true` cleanly.
    // Pattern A1 in the same bucketing was 20.4% Instagram WebView —
    // unblocks finer-grained filtering when re-running the analysis.
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    event.tags = { ...event.tags, is_instagram: /Instagram/i.test(ua) };

    return event;
  },

  beforeBreadcrumb: filterBreadcrumb,

  ignoreErrors: [
    'ResizeObserver loop',
    'Non-Error promise rejection',
    /Loading chunk \d+ failed/,
    'Java object is gone',
    'Java exception was raised during method invocation',
  ],
});
